import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import BASE_URL from '../../constants/Config';

// Base API URL
const FULL_BASE_URL = `${BASE_URL}/api/Comment`;

// Types
interface Comment {
  id: number;
  comment: string;
  name: string;
  surname: string;
  companyName: string;
  email: string;
  projectId: number;
  status: string;
}

interface CommentState {
  comments: Comment[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

interface SaveCommentRequest {
  projectId: number;
  companyName: string;
  name: string;
  surname: string;
  email: string;
  comment: string;
}

interface ApproveCommentRequest {
  token: string;
  id: number;
}

interface DeleteCommentRequest {
  commentId: number;
  token: string;
}

// Initial State
const initialState: CommentState = {
  comments: [],
  status: 'idle',
  error: null,
};

// Async Thunks
export const saveComment = createAsyncThunk<
  boolean,
  SaveCommentRequest,
  { rejectValue: string }
>('comments/saveComment', async (data, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${FULL_BASE_URL}/save`, data);
    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = typeof axiosError.response?.data === 'string' 
      ? axiosError.response.data 
      : 'An error occurred';
    return rejectWithValue(errorMessage);
  }
});

export const approveComment = createAsyncThunk<
  boolean,
  ApproveCommentRequest,
  { rejectValue: string }
>('comments/approveComment', async ({ token, id }, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${FULL_BASE_URL}/approve`, { token, id });
    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = typeof axiosError.response?.data === 'string' 
      ? axiosError.response.data 
      : 'An error occurred';
    return rejectWithValue(errorMessage);
  }
});

export const deleteComment = createAsyncThunk<
  boolean,
  DeleteCommentRequest,
  { rejectValue: string }
>('comments/deleteComment', async ({token , commentId }, { rejectWithValue }) => {
  try {
    const response = await axios.delete(`${FULL_BASE_URL}/delete`, {
      data: { token ,commentId },
    });
    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = typeof axiosError.response?.data === 'string' 
      ? axiosError.response.data 
      : 'An error occurred';
    return rejectWithValue(errorMessage);
  }
});

export const fetchAllComments = createAsyncThunk<
  Comment[],
  void,
  { rejectValue: string }
>('comments/fetchAllComments', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${FULL_BASE_URL}/findall`);
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = typeof axiosError.response?.data === 'string' 
      ? axiosError.response.data 
      : 'An error occurred';
    return rejectWithValue(errorMessage);
  }
});

export const fetchCommentsByProjectId = createAsyncThunk<
  Comment[],
  number,
  { rejectValue: string }
>('comments/fetchCommentsByProjectId', async (projectId, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${FULL_BASE_URL}/findallbyprojectid`, {
      params: { projectId },
    });
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = typeof axiosError.response?.data === 'string' 
      ? axiosError.response.data 
      : 'An error occurred';
    return rejectWithValue(errorMessage);
  }
});

// Slice
const commentSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Save Comment
      .addCase(saveComment.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveComment.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(saveComment.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to save comment';
      })

      // Approve Comment
      .addCase(approveComment.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(approveComment.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(approveComment.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to approve comment';
      })

      // Delete Comment
      .addCase(deleteComment.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteComment.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to delete comment';
      })

      // Fetch All Comments
      .addCase(fetchAllComments.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAllComments.fulfilled, (state, action: PayloadAction<Comment[]>) => {
        state.status = 'succeeded';
        state.comments = action.payload;
      })
      .addCase(fetchAllComments.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch comments';
      })

      // Fetch Comments by Project ID
      .addCase(fetchCommentsByProjectId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCommentsByProjectId.fulfilled, (state, action: PayloadAction<Comment[]>) => {
        state.status = 'succeeded';
        state.comments = action.payload;
      })
      .addCase(fetchCommentsByProjectId.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch comments by project ID';
      });
  },
});

export default commentSlice.reducer;
